import i18next from 'i18next';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  fetchPatient,
  fetchPatientTimeline,
  fetchWorkspacePatients,
} from '@/api/patients/patientsApi';
import type {
  Patient,
  PatientSummary,
  PatientTimelinePage,
} from '@/api/patients/patientsApi.types';
import type { PaginatedResult } from '@/api/pagination.types';
import { ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import {
  PATIENT_TIMELINE_FIRST_PAGE_SIZE,
  PATIENT_TIMELINE_OLDER_PAGE_SIZE,
} from '@/constants/patientTimelinePagination';
import {
  PATIENTS_QUERY_KEY,
  buildPatientQueryKey,
  buildPatientTimelineQueryKey,
} from '@/constants/queryKeys';

export function useWorkspacePatientsQuery(
  page: number,
  search: string
): UseQueryResult<PaginatedResult<PatientSummary>, Error> {
  return useQuery({
    queryKey: [...PATIENTS_QUERY_KEY, 'list', i18next.language, page, search],
    queryFn: function loadPatients() {
      return fetchWorkspacePatients(page, ADMIN_LIST_PAGE_SIZE, search);
    },
    placeholderData: keepPreviousData,
  });
}

export function usePatientQuery(patientId: string): UseQueryResult<Patient, Error> {
  return useQuery({
    queryKey: [...buildPatientQueryKey(patientId), i18next.language],
    queryFn: function loadPatient() {
      return fetchPatient(patientId);
    },
  });
}

export function usePatientTimelineQuery(
  patientId: string
): UseInfiniteQueryResult<InfiniteData<PatientTimelinePage>, Error> {
  return useInfiniteQuery({
    queryKey: [...buildPatientTimelineQueryKey(patientId), i18next.language],
    queryFn: function loadTimelinePage({ pageParam }) {
      const pageSize =
        pageParam === null ? PATIENT_TIMELINE_FIRST_PAGE_SIZE : PATIENT_TIMELINE_OLDER_PAGE_SIZE;
      return fetchPatientTimeline(patientId, pageSize, pageParam);
    },
    initialPageParam: null as string | null,
    getNextPageParam: function resolveNextPageParam(lastPage) {
      return lastPage.nextCursor;
    },
    enabled: patientId.length > 0,
  });
}
